import * as _ from "lodash";
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import { ICompletionTypes, ICustomMonarchToken } from "../model/model";
import { UserSnippet } from "../../../processEditor/actions/SnippetActions";
import { ThemeType } from "shared/styles/theme";

export const tokenColor = (theme: ThemeType) => theme === ThemeType.light ? "0052D9" : "569cd6";
export const theme = "frendsTheme";

export const buildDependencyProposals = (range: monaco.IRange, completionTypes: ICompletionTypes, snippets: UserSnippet[]): monaco.languages.CompletionItem[] => {
    // Build a completion list based on what kind on input we are dealing with i.e. TriggerInput, ParameterEditor etc.
    const completionItemList: monaco.languages.CompletionItem[] = [];
    const addToCompletionItemList = (
        prefix: string,
        completionItemName: string | null,
        sortText: string, // Char representing in which order we show completions, we want return and var to be highest in the list
        insertText?: string
    ) => {
        switch (prefix) {
            case "result": {
                completionItemList.push({
                    label: `#result`,
                    insertText: `#result`,
                    kind: monaco.languages.CompletionItemKind.Variable,
                    range,
                    sortText: "a"
                })
                completionItemList.push({
                    label: `#result[${completionItemName}]`,
                    insertText: `#result[${completionItemName}]`,
                    kind: monaco.languages.CompletionItemKind.Variable,
                    range,
                    sortText
                })
                break;
            }
            case "snippet": {
                if (insertText) {
                    completionItemList.push({
                        label: `#${prefix}.${completionItemName}`,
                        insertText: insertText,
                        kind: 15, // CompletionItemKind.Snippet should be 15, but is wrongly set to 27.
                        range,
                        sortText
                    })
                }
                break;
            }
            case "env": {
                completionItemList.push({
                    label: `#${prefix}.${completionItemName}`,
                    insertText: `#${prefix}.${completionItemName}`,
                    kind: monaco.languages.CompletionItemKind.Variable,
                    range,
                    sortText
                })
                break;
            }
            case "api": {
                completionItemList.push({
                    label: `#${prefix}.${completionItemName}`,
                    insertText: `#${prefix}.${completionItemName}`,
                    kind: monaco.languages.CompletionItemKind.Variable,
                    range,
                    sortText
                })
                break;
            }
            default: {
                completionItemList.push({
                    label: `#${prefix}.${completionItemName}`,
                    insertText: `#${prefix}.${completionItemName}`,
                    kind: monaco.languages.CompletionItemKind.Variable,
                    range,
                    sortText
                })
                break;
            }
        }
    }
    if (!_.isEmpty(completionTypes.variables)) {
        for (const variable of completionTypes.variables) {
            addToCompletionItemList("var", variable.variableName, "a")
        }
    }
    if (!_.isEmpty(completionTypes.processVariables)) {
        for (const processVariable of completionTypes.processVariables) {
            addToCompletionItemList("var", processVariable, "a")
        }
    }
    if (!_.isEmpty(completionTypes.returnVariables)) {
        for (const returnVariable of completionTypes.returnVariables) {
            addToCompletionItemList("result", returnVariable.variableName, "b")
        }
    }
    if (!_.isEmpty(snippets)) {
        for (const snippet of snippets) {
            addToCompletionItemList("snippet", snippet.name, "c", snippet.snippet)
        }
    }
    if (!_.isEmpty(completionTypes.triggers)) {
        for (const trigger of completionTypes.triggers) {
            addToCompletionItemList("trigger", trigger, "d")
        }
    }
    if (!_.isEmpty(completionTypes.processReferences)) {
        for (const processReference of completionTypes.processReferences) {
            addToCompletionItemList("process", processReference, "e")
        }
    }
    if (!_.isEmpty(completionTypes.environmentVariables)) {
        for (const envVar of completionTypes.environmentVariables) {
            addToCompletionItemList("env", envVar, "f")
        }
    }
    if (!_.isEmpty(completionTypes.apiSpecPaths)) {
        for (const apiSpecPath of completionTypes.apiSpecPaths) {
            addToCompletionItemList("api", apiSpecPath, "a")
        }
    }
    const uniqueCompletionItemList = _.uniqBy(completionItemList, "label");
    return uniqueCompletionItemList;
}

const handlebarMonarchTokens: ICustomMonarchToken[] = [
    {
        token: "handlebarsVariable",
        regex: /({{#(\w+)(?:(\.[\wåÅäÄöÖ]+)(?![\wåÅäÄöÖ]*\()){1,2}}})/
    }
];

export const allMonarchTokens: ICustomMonarchToken[] = [
    ...handlebarMonarchTokens,
    {
        token: "environmentVariable",
        regex: /(?:\s*)#env(?:(\.[\wåÅäÄöÖ]+)(?![\wåÅäÄöÖ]*\()){1,2}/   //Highlighting should work for 2 levels, ignore function calls
    },
    {
        token: "trigger",
        regex: /(?:\s*)#(trigger)(?:(\.[\wåÅäÄöÖ]+)(?![\wåÅäÄöÖ]*\())+/
    },
    {
        token: "process",
        regex: /(?:\s*)#(process)(?:(\.[\wåÅäÄöÖ]+)(?![\wåÅäÄöÖ]*\())+/
    },
    {
        token: "resultAndVariable",
        regex: /(?:\s*)#(result|var)(?:(\[[^\]]*\]|(\.[\wåÅäÄöÖ]+))+)?/ 
    },
    {
        token: "apiSpecPath",
        regex: /(?:\s*)#(api)(?:(\.[\wåÅäÄöÖ]+)(?![\wåÅäÄöÖ]*\())+/
    },
];

export const registerMonarchTokens = async (monacoInstance: typeof monaco) => {
    const languagesToExtend = ["csharp", "xml", "sql", "handlebars", "markdown"];
    // Add our custom monarch tokens directly into monaco csharp language to preserve csharp functionality, doesn't work the other way around. 
    const mappedTokens: Array<monaco.languages.IMonarchLanguageRule> = allMonarchTokens.map((monarch) => ([monarch.regex, monarch.token]))
    const customMonarchTokens: monaco.languages.IMonarchLanguage = {
        "tokenizer": {
            "root": [
                { include: "custom" }
            ],
            "custom": mappedTokens
        },
        ignoreCase: true
    };
    const allLangs = monacoInstance.languages.getLanguages();
    for (const languageToExtend of languagesToExtend) {
        // @ts-expect-error: .loader() method is not typed.
        const { language } = await allLangs.find(({ id }) => id === languageToExtend).loader();
        for (const key in customMonarchTokens) {
            const value = customMonarchTokens[key];
            if (key === "tokenizer") {
                for (const category in value) {
                    const tokenDefs = value[category];
                    if (!Object.prototype.hasOwnProperty.call(language.tokenizer, category)) {
                        language.tokenizer[category] = [];
                    }
                    if (Array.isArray(tokenDefs)) {
                        language.tokenizer[category].unshift(...tokenDefs)
                    }
                }
            } else if (key === "ignoreCase") {
                language[key] = value;
            } else if (Array.isArray(value)) {
                if (!Object.prototype.hasOwnProperty.call(language, key)) {
                    language[key] = [];
                }
                language[key].unshift(...value)
            }
        }
    }
}

export const setJSONDiagnosticOptions = (monacoInstance: typeof monaco) => {
    // configure the JSON language support with schemas and schema associations
    monacoInstance.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: false
    });
}

export const filterSnippets = (language: string, snippets: UserSnippet[]): UserSnippet[] => {
    const snippetsWithAllMode = snippets.filter((f) => f.languageMode === "All");
    switch (language) {
        case "csharp": {
            return snippetsWithAllMode.concat(snippets.filter((f) => f.languageMode === "Expression"));
        }
        case "xml": {
            return snippetsWithAllMode.concat(snippets.filter((f) => f.languageMode === "XML"));
        }
        case "sql": {
            return snippetsWithAllMode.concat(snippets.filter((f) => f.languageMode === "SQL"));
        }
        case "json": {
            return snippetsWithAllMode.concat(snippets.filter((f) => f.languageMode === "JSON"));
        }
        case "plaintext":
        case "handlebars": {
            return snippetsWithAllMode.concat(snippets.filter((f) => f.languageMode === "Text"));
        }
        default: {
            return snippets;
        }
    }
}

export const getTokenRules = (lang: string, theme: ThemeType): monaco.editor.ITokenThemeRule[] => {
    const runTokenThemeRuleMapping = (tokens: ICustomMonarchToken[]) => tokens.map((x) => ({ token: x.token, foreground: tokenColor(theme) }))
    if (lang === "handlebars") {
        return runTokenThemeRuleMapping(handlebarMonarchTokens);
    } else {
        return runTokenThemeRuleMapping(allMonarchTokens)
    }
}